import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pill, FlaskConical, Search, BadgeCheck } from "lucide-react";

export default function MedicineLabDirectory() {
  const { user } = useAuth();
  const [tab, setTab] = useState("medicines");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);
  const isGp = (user?.specialty || "").toLowerCase().includes("mbbs") || (user?.specialty || "").toLowerCase().includes("general physician") || !user?.specialty;

  const load = (query, t) => {
    setLoading(true);
    api.get(t === "medicines" ? "/medicines" : "/labtests", { params: { q: query || undefined, limit: 40 } })
      .then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load("", tab); }, [tab]);
  const onSearch = (v) => {
    setQ(v);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(v, tab), 250);
  };

  return (
    <div className="space-y-5" data-testid="inventory-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-2">
          <Pill2 active={tab === "medicines"} onClick={() => { setTab("medicines"); setQ(""); }} icon={Pill} label="Medicines" testId="dir-tab-medicines" />
          <Pill2 active={tab === "labtests"} onClick={() => { setTab("labtests"); setQ(""); }} icon={FlaskConical} label="Lab Tests" testId="dir-tab-labtests" />
        </div>
        <span className="text-xs text-slate-500 bg-white border border-slate-200 rounded-full px-3 py-1.5">
          {isGp ? "Showing GP-level formulary" : "Showing full specialist formulary"}
        </span>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input data-testid="input-directory-search" value={q} onChange={(e) => onSearch(e.target.value)} placeholder={tab === "medicines" ? "Search generic medicine name…" : "Search lab test…"} className="w-full pl-9 pr-3 py-3 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#1E5AA8]/30" />
      </div>

      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          {tab === "medicines" ? (
            <table className="w-full text-sm">
              <thead className="bg-[#EBF3FB] text-slate-600 text-xs">
                <tr><th className="text-left p-3">Generic Name</th><th className="text-left p-3">Pack</th><th className="text-left p-3">Level</th></tr>
              </thead>
              <tbody data-notranslate>
                {items.map((m, i) => (
                  <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/60">
                    <td className="p-3 font-medium text-slate-800">{m.name}</td>
                    <td className="p-3 text-slate-500">{m.unit_size}</td>
                    <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${m.level === "gp" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>{m.level === "gp" ? "GP" : "Specialist"}</span></td>
                  </tr>
                ))}
                {!loading && items.length === 0 && <tr><td colSpan={3} className="p-10 text-center text-slate-400">No medicines found.</td></tr>}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#EBF3FB] text-slate-600 text-xs">
                <tr><th className="text-left p-3">Test Name</th><th className="text-left p-3">Category</th><th className="text-left p-3">Availability</th><th className="text-left p-3">Level</th></tr>
              </thead>
              <tbody data-notranslate>
                {items.map((t, i) => (
                  <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/60">
                    <td className="p-3 font-medium text-slate-800">{t.name}</td>
                    <td className="p-3 text-slate-500">{t.category}</td>
                    <td className="p-3 text-xs text-slate-500">
                      {t.available_central_lab && <span className="inline-flex items-center gap-1 text-emerald-600"><BadgeCheck className="w-3 h-3" />Central Lab</span>}
                      {!t.available_central_lab && <span className="text-slate-400">Referral Lab</span>}
                    </td>
                    <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full ${t.level === "gp" ? "bg-blue-50 text-blue-700" : "bg-purple-50 text-purple-700"}`}>{t.level === "gp" ? "GP" : "Specialist"}</span></td>
                  </tr>
                ))}
                {!loading && items.length === 0 && <tr><td colSpan={4} className="p-10 text-center text-slate-400">No tests found.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Pill2({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button data-testid={testId} onClick={onClick} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${active ? "bg-[#1E5AA8] text-white shadow-sm" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}
